import { useCallback, useMemo } from 'react'
import { formatTicketDate, getTicketStatusLabel, ticketStatusBadgeVariants } from './util'
import MemberTicketCommentView from './MemberTicketCommentView'
import useTicketDetails from 'core/hooks/member/useTicketDetails'
import useTicketComments from 'core/hooks/member/useTicketComments'
import CrisisNoteDetailsView from './CrisisNoteDetailsView'
import CaseManagementDetailsView from './CaseManagementDetailsView'
import CareCoordinationDetailsView from './CareCoordinationDetailsView'
import Badge from 'components/common/Badge'
import type { Ticket } from './types'

export enum NoteType {
  SandboxCrisisNote = 'SANDBOX TIMELYCARE CRISIS SUPPORT SANDBOX',
  PreProductionCrisisNote = 'TEST TIMELYCARE CRISIS SUPPORT TEST',
  ProductionCrisisNote = 'TIMELYCARE CRISIS SUPPORT',
  SandboxCaseManagement = 'SANDBOX TIMELYCARE CASE MANAGEMENT SANDBOX',
  PreProductionCaseManagement = 'TEST TIMELYCARE CASE MANAGEMENT TEST',
  ProductionCaseManagement = 'TIMELYCARE CASE MANAGEMENT',
  SandboxCareCoordination = 'SANDBOX TIMELYCARE CARE COORDINATION SANDBOX',
  PreProductionCareCoordination = 'TEST TIMELYCARE CARE COORDINATION TEST',
  ProductionCareCoordination = 'TIMELYCARE CARE COORDINATION',
}

type MemberTicketDetailsViewProps = {
  ticketId: string
  ticketData: Ticket
}

const MemberTicketDetailsView = ({ ticketId, ticketData }: MemberTicketDetailsViewProps) => {
  const { ticketDetails, isLoading: isLoadingDetails, isError: isErrorDetails } = useTicketDetails(ticketId)
  const {
    ticketComments,
    isLoading: isLoadingComments,
    isError: isErrorComments,
  } = useTicketComments(ticketDetails?.noteId)

  const { externalUpdatedAt, status, externalTicketId, noteType, assignee } = ticketData || { status: 'new' }

  const displayUpdatedAt = useMemo(() => formatTicketDate(externalUpdatedAt), [externalUpdatedAt])

  const renderTicketDetails = useCallback(() => {
    const upperNoteType = noteType?.toUpperCase() || ''

    switch (upperNoteType) {
      case NoteType.SandboxCrisisNote:
      case NoteType.PreProductionCrisisNote:
      case NoteType.ProductionCrisisNote:
        return <CrisisNoteDetailsView details={ticketDetails} assignee={assignee} ticketData={ticketData} />
      case NoteType.SandboxCaseManagement:
      case NoteType.PreProductionCaseManagement:
      case NoteType.ProductionCaseManagement:
        return <CaseManagementDetailsView details={ticketDetails} assignee={assignee} ticketData={ticketData} />
      case NoteType.SandboxCareCoordination:
      case NoteType.PreProductionCareCoordination:
      case NoteType.ProductionCareCoordination:
        return <CareCoordinationDetailsView details={ticketDetails} assignee={assignee} ticketData={ticketData} />
      default:
        return (
          <div className="p-m text-neutral-500">
            Unknown ticket type: {noteType}. Please contact support if you believe this is an error.
          </div>
        )
    }
  }, [noteType, ticketDetails, assignee, ticketData])

  if (isLoadingDetails || isLoadingComments) {
    return <div className="p-m">Loading ticket details...</div>
  }

  if (isErrorDetails || isErrorComments) {
    return <div className="p-m text-error-500">Error loading ticket details. Please try again.</div>
  }

  if (!ticketDetails || Object.keys(ticketDetails).length === 0) {
    return <div className="p-m">No ticket details found.</div>
  }

  return (
    <div className="shadow-1 flex flex-col gap-6 rounded-2xl bg-white text-neutral-800">
      <div className="flex flex-row flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <h2 className="typography-h4 flex shrink-0 flex-row items-center gap-4">
          Ticket Details <b className="typography-body-l font-medium text-neutral-500">#{externalTicketId}</b>
        </h2>
        <p className="typography-body flex flex-row items-center gap-x-4 gap-y-2">
          <span className="whitespace-pre">
            <b className="mr-2 font-semibold text-neutral-500">Updated:</b>
            {displayUpdatedAt}
          </span>
          <Badge variant={ticketStatusBadgeVariants[status] || 'neutral'}>{getTicketStatusLabel(status)}</Badge>
        </p>
      </div>

      {renderTicketDetails()}

      {!!ticketComments?.length && (
        <>
          <hr />
          <h2 className="typography-h4">Comments</h2>
          {ticketComments.map((comment) => (
            <MemberTicketCommentView key={comment.commentId} comment={comment} />
          ))}
        </>
      )}
    </div>
  )
}

export default MemberTicketDetailsView
